import Swal from "sweetalert2";
import { wencesApi } from "@/api/wencesApi";
import { creditNoteShippingSaveErrorKind } from "@/modules/nota_comercial_module/utils/notaComercialSaveErrors";
import notaComercialUnapplyMixin from "@/modules/nota_comercial_module/mixins/notaComercialUnapplyMixin";
import { notaComercialModalContextFromExpenseLine } from "@/modules/nota_comercial_module/utils/notaComercialFromExpenseLine";

const FOB_NC_RULES = {
  client_credit_ours: {
    credit_debit: "Credit note",
    flow_direction: "Giving",
    contact_role: "Client",
  },
  client_debit_ours: {
    credit_debit: "Debit note",
    flow_direction: "Giving",
    contact_role: "Client",
  },
  supplier_credit_to_us: {
    credit_debit: "Credit note",
    flow_direction: "Receiving",
    contact_role: "Supplier",
  },
  supplier_debit_to_us: {
    credit_debit: "Debit note",
    flow_direction: "Receiving",
    contact_role: "Supplier",
  },
};

function localCalendarIsoDate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default {
  mixins: [notaComercialUnapplyMixin],
  computed: {
    /** Cobros, pagos o administración financiera: cualquiera habilita los atajos de nota comercial en FOB / Finanzas. */
    fobNcShortcutUiAllowed() {
      if (!this.fobNotaComercialShortcuts) return false;
      return !!(this.canCollect || this.canMakePayments || this.canFinanceManagePayments);
    },
    /** Notas comerciales (cliente) con applied_to solo a la proforma (paso 1). */
    fobNcLinkedClientNotesProform() {
      const fd = this.financialData;
      const bag = fd && fd.linkedNotasComerciales;
      return bag && Array.isArray(bag.clientProform) ? bag.clientProform : [];
    },
    fobNcLinkedClientNotesProformCreditOnly() {
      return this.fobNcLinkedClientNotesProform.filter((n) => this.fobNcNoteAppliedKind(n) === "credit");
    },
    fobNcLinkedClientNotesProformDebitOnly() {
      return this.fobNcLinkedClientNotesProform.filter((n) => this.fobNcNoteAppliedKind(n) === "debit");
    },
    fobNcEditAppliedHintText() {
      const ctx = this.fobNcEditContext;
      if (!ctx) return "";
      if (ctx.mode === "proform") {
        const name =
          (this.itemData && this.itemData.name && String(this.itemData.name).trim()) ||
          (ctx.proformId ? String(ctx.proformId) : "—");
        return this.$t("financialSummary.fobNotaComercial.editHintProform", { name });
      }
      return this.$t("financialSummary.fobNotaComercial.editHintShipping");
    },
  },
  data() {
    return {
      fobNcExtraLists: [],
      fobNcSaving: false,
      /** Edición / borrado de nota existente (popup). */
      showFobNcEditModal: false,
      fobNcEditSaving: false,
      fobNcEditDeleting: false,
      fobNcEditFetching: false,
      fobNcEditDoc: null,
      fobNcEditAmount: "",
      fobNcEditDate: "",
      fobNcEditCurrencyId: "",
      fobNcEditComment: "",
      /** @type {{ mode: 'proform' | 'shipping'; proformId?: string; shippingId?: string } | null} */
      fobNcEditContext: null,
    };
  },
  methods: {
    fobNcCurrencyListDoc() {
      const fromProps = (this.lists || []).find((l) => l && l.type === "currency");
      if (fromProps && Array.isArray(fromProps.values) && fromProps.values.length) {
        return fromProps;
      }
      return (this.fobNcExtraLists || []).find((l) => l && l.type === "currency") || null;
    },
    fobNcCurrencyRows() {
      const doc = this.fobNcCurrencyListDoc();
      if (!doc || !Array.isArray(doc.values)) return [];
      return doc.values.filter((v) => v && v._id);
    },
    fobNcDefaultCurrencyId() {
      const rows = this.fobNcCurrencyRows();
      if (!rows.length) return "";
      const re = /\busd\b|u\.s\.?\s*d|\$\s*us|^\s*usd\s*$/i;
      const byLabel = rows.find((v) => re.test(String(v.label || "")));
      if (byLabel) return String(byLabel._id);
      const byCode = rows.find((v) => re.test(String(v.code || v.short || v.symbol || "")));
      if (byCode) return String(byCode._id);
      return String(rows[0]._id);
    },
    async fobNcEnsureLists() {
      if (this.fobNcCurrencyRows().length) return;
      try {
        const { data } = await wencesApi.get("/api/gotrading/lists");
        this.fobNcExtraLists = Array.isArray(data) ? data : [];
      } catch {
        this.fobNcExtraLists = [];
      }
    },
    fobNcUnwrapDynamicGet(res) {
      const d = res && res.data;
      if (d && d.data != null && typeof d.data === "object") return d.data;
      if (d && typeof d === "object") return d;
      return null;
    },
    fobNcRefId(val) {
      if (val == null || val === "") return "";
      if (typeof val === "object" && val._id != null) return String(val._id);
      return String(val).trim();
    },
    fobNcDateToInput(val) {
      if (!val) return "";
      const s = String(val);
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
      return s.slice(0, 10);
    },
    /**
     * Efecto de la nota sobre lo pendiente del cliente: `credit` lo reduce, `debit` lo aumenta.
     * En las notas que nos emite el cliente no coincide con `credit_debit`, por eso el backend
     * manda `appliedKind`; el fallback cubre payloads viejos (solo Dar + Cliente).
     */
    fobNcNoteAppliedKind(n) {
      const kind = n && n.appliedKind;
      if (kind === "credit" || kind === "debit") return kind;
      return String(n && n.credit_debit) === FOB_NC_RULES.client_credit_ours.credit_debit
        ? "credit"
        : "debit";
    },
    /** Solo NC cliente (etiqueta en inglés en BD). */
    fobNcLinkedNoteIsCreditNote(n) {
      return String(n && n.credit_debit) === FOB_NC_RULES.client_credit_ours.credit_debit;
    },
    fobNcAppliedLineMatchesEditContext(line, ctx) {
      if (!line || !ctx) return false;
      const shipId = this.fobNcRefId(line.shipping);
      const profId = this.fobNcRefId(line.proform);
      if (ctx.mode === "proform") {
        return (
          !!profId &&
          String(profId) === String(ctx.proformId) &&
          (!shipId || shipId === "")
        );
      }
      if (ctx.mode === "shipping") {
        return !!shipId && String(shipId) === String(ctx.shippingId);
      }
      return false;
    },
    /** Arrastre desde NC cliente aplicada solo a proforma → soltar en embarque (misma UX que cobros). */
    onNotaProformDragStart(ev, noteSummary) {
      if (!this.fobNcShortcutUiAllowed || this.redistributeInProgress) {
        ev.preventDefault();
        return;
      }
      if (!noteSummary || !noteSummary._id) {
        ev.preventDefault();
        return;
      }
      try {
        const maxAmount = Math.max(0, Number(noteSummary.appliedTotal) || 0);
        ev.dataTransfer.setData(
          "application/x-gotrading-nota-proform",
          JSON.stringify({
            kind: "nota_proform",
            notaId: String(noteSummary._id),
            maxAmount,
          })
        );
        ev.dataTransfer.effectAllowed = "move";
        this.stagesDndActive = true;
        this.stagesDndCollectKind = null;
        this.stagesDndNotaMode = "proform_to_ship";
        this.stagesDndFromStage2 = false;
      } catch (e) {
        ev.preventDefault();
      }
    },
    onNotaProformDragEnd() {
      if (typeof this.clearStagesDndHighlight === "function") {
        this.clearStagesDndHighlight();
      }
    },
    /**
     * Línea aplicada a este embarque en el resumen FOB: se puede devolver a solo-proforma (quitar `shipping`).
     * Incluye líneas solo-embarque sin `proform` creadas en este flujo (se fija `proform` al volver).
     */
    fobNcLineMatchesShippingDragReturn(line, shippingId, proformId) {
      const sid = this.fobNcRefId(line.shipping);
      if (!sid || String(sid) !== String(shippingId)) return false;
      const prof = this.fobNcRefId(line.proform);
      if (!prof || prof === "") return true;
      return String(prof) === String(proformId);
    },
    onNotaShippingDragStart(ev, noteSummary, ship) {
      if (!this.fobNcShortcutUiAllowed || this.redistributeInProgress) {
        ev.preventDefault();
        return;
      }
      if (!noteSummary || !noteSummary._id || !ship || ship.shippingId == null || ship.shippingId === "") {
        ev.preventDefault();
        return;
      }
      try {
        const maxAmount = Math.max(0, Number(noteSummary.appliedTotal) || 0);
        ev.dataTransfer.setData(
          "application/x-gotrading-nota-shipping",
          JSON.stringify({
            kind: "nota_shipping",
            notaId: String(noteSummary._id),
            shippingId: String(ship.shippingId),
            maxAmount,
          })
        );
        ev.dataTransfer.effectAllowed = "move";
        this.stagesDndActive = true;
        this.stagesDndCollectKind = null;
        this.stagesDndNotaMode = "shipping_to_proform";
        this.stagesDndFromStage2 = true;
      } catch (e) {
        ev.preventDefault();
      }
    },
    fobNcSumProformOnlyApplied(doc, proformId) {
      const ctx = { mode: "proform", proformId: String(proformId) };
      const lines = Array.isArray(doc.applied_to) ? doc.applied_to : [];
      return lines.reduce((s, line) => {
        if (!this.fobNcAppliedLineMatchesEditContext(line, ctx)) return s;
        const m = Number(line.amount);
        return s + (Number.isFinite(m) && !Number.isNaN(m) ? m : 0);
      }, 0);
    },
    /** Suma importes en `applied_to` para este embarque (mismo criterio que al volver a proforma). */
    fobNcSumShippingMatchApplied(doc, fromShip, proformId) {
      const lines = Array.isArray(doc.applied_to) ? doc.applied_to : [];
      return lines.reduce((s, line) => {
        if (!this.fobNcLineMatchesShippingDragReturn(line, fromShip, proformId)) return s;
        const m = Number(line.amount);
        return s + (Number.isFinite(m) && !Number.isNaN(m) ? m : 0);
      }, 0);
    },
    /** Reparto parcial: solo-proforma → proforma + embarque. */
    fobNcBuildLinesPartialProformToShipping(linesRaw, proformId, shippingId, amountToAssign) {
      const EPS = 0.005;
      const ctx = { mode: "proform", proformId: String(proformId) };
      let rem = Number(amountToAssign) || 0;
      if (rem < EPS) return null;
      const out = [];
      for (const line of linesRaw) {
        if (!this.fobNcAppliedLineMatchesEditContext(line, ctx)) {
          out.push({ ...line });
          continue;
        }
        const m = Number(line.amount) || 0;
        if (m < EPS) {
          out.push({ ...line });
          continue;
        }
        if (rem < EPS) {
          out.push({ ...line });
          continue;
        }
        if (rem >= m - EPS) {
          out.push({ ...line, shipping: shippingId });
          rem -= m;
        } else {
          const keepAmt = m - rem;
          out.push({ ...line, amount: keepAmt });
          out.push({
            proform: String(proformId),
            shipping: String(shippingId),
            amount: rem,
          });
          rem = 0;
        }
      }
      if (rem > EPS) return null;
      return out;
    },
    /** Reparto parcial: embarque → solo-proforma. */
    fobNcBuildLinesPartialShippingToProform(linesRaw, fromShippingId, proformId, amountToMove) {
      const EPS = 0.005;
      let rem = Number(amountToMove) || 0;
      if (rem < EPS) return null;
      const out = [];
      for (const line of linesRaw) {
        if (!this.fobNcLineMatchesShippingDragReturn(line, fromShippingId, proformId)) {
          out.push({ ...line });
          continue;
        }
        const m = Number(line.amount) || 0;
        if (m < EPS) {
          out.push({ ...line });
          continue;
        }
        if (rem < EPS) {
          out.push({ ...line });
          continue;
        }
        const prof = this.fobNcRefId(line.proform) || String(proformId);
        if (rem >= m - EPS) {
          out.push({
            ...line,
            shipping: null,
            proform: prof,
          });
          rem -= m;
        } else {
          const stayOnShip = m - rem;
          out.push({
            ...line,
            amount: stayOnShip,
          });
          out.push({
            proform: prof,
            shipping: null,
            amount: rem,
          });
          rem = 0;
        }
      }
      if (rem > EPS) return null;
      return out;
    },
    async fobNcPutNotaWithAppliedLines(nid, doc, updatedLinesRaw) {
      const sumLines = updatedLinesRaw.reduce((s, l) => {
        const m = Number(l.amount);
        return s + (Number.isFinite(m) && !Number.isNaN(m) ? m : 0);
      }, 0);
      const applied_to = updatedLinesRaw.map((l) => ({
        ...(l._id ? { _id: l._id } : {}),
        shipping: this.fobNcRefId(l.shipping) || null,
        proform: this.fobNcRefId(l.proform) || null,
        amount: Number(l.amount) || 0,
      }));
      const headerAmt = Number(doc.amount);
      const payload = {
        name: doc.name != null ? String(doc.name) : "",
        date: this.fobNcDateToInput(doc.date) || localCalendarIsoDate(),
        contact: this.fobNcRefId(doc.contact),
        currency: this.fobNcRefId(doc.currency) || null,
        credit_debit: doc.credit_debit,
        flow_direction: doc.flow_direction,
        contact_role: doc.contact_role,
        amount: sumLines > 0.005 ? sumLines : (Number.isFinite(headerAmt) ? headerAmt : 0),
        comment: doc.comment != null ? String(doc.comment) : "",
        applied_to,
      };
      await wencesApi.put(`/api/gotrading/dynamic/notacomercial/${encodeURIComponent(nid)}`, payload);
    },
    /** Mismo popup de importe que `promptAdvanceRedistribute` (cobros). */
    async fobNcSwalRedistributeAmount(htmlContent, effectiveMax) {
      const EPS = 0.005;
      if (!(effectiveMax > EPS)) return null;
      const defaultAmount = parseFloat(effectiveMax.toFixed(2));
      const { value } = await Swal.fire({
        title: this.$tn("financialSummary.stages.redistributeTitle"),
        html: htmlContent,
        input: "number",
        inputValue: String(defaultAmount),
        showCancelButton: true,
        confirmButtonText: this.$tn("financialSummary.stages.redistributeConfirm"),
        cancelButtonText: this.$tn("financialSummary.close"),
        inputAttributes: {
          step: "0.01",
          min: "0.01",
          max: String(defaultAmount),
        },
        preConfirm: (v) => {
          const n = Number(v);
          if (!Number.isFinite(n) || n < 0.01) {
            Swal.showValidationMessage(this.$tn("financialSummary.stages.redistributeInvalid"));
            return false;
          }
          if (n > effectiveMax + 0.001) {
            Swal.showValidationMessage(
              this.$tn("financialSummary.stages.redistributeTooMuch", {
                max: (this.moneyPrefix || "$") + this.formatNumber(effectiveMax),
              })
            );
            return false;
          }
          return n;
        },
      });
      if (value === undefined || value === null) return null;
      return Number(value);
    },
    /**
     * Igual que cobros: tope según zona, HTML explicativo y Swal con importe antes del PUT.
     * @param {{ kind: string; notaId: string; maxAmount?: number }} parsed
     * @param {{ shippingId: string|number }} ship
     * @param {'advance'|'invoice'} shipDropZone
     */
    async promptNotaProformLinesToShipping(parsed, ship, shipDropZone) {
      const nid = parsed && parsed.notaId != null ? String(parsed.notaId).trim() : "";
      if (!nid || !ship || ship.shippingId == null || ship.shippingId === "") return;
      if (!this.fobNcShortcutUiAllowed) return;
      const proformId =
        this.itemData && this.itemData._id != null && this.itemData._id !== ""
          ? String(this.itemData._id)
          : "";
      if (!proformId) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text: this.$tn("financialSummary.fobNotaComercial.needProform"),
        });
        return;
      }
      const targetShipping = String(ship.shippingId);
      const zone = shipDropZone === "invoice" ? "invoice" : "advance";
      try {
        const res = await wencesApi.get(`/api/gotrading/dynamic/notacomercial/${encodeURIComponent(nid)}`);
        const doc = this.fobNcUnwrapDynamicGet(res);
        if (!doc || !doc._id) {
          await Swal.fire({
            icon: "error",
            title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
            text: this.$tn("financialSummary.fobNotaComercial.editLoadFailed"),
          });
          return;
        }
        const sumProform = this.fobNcSumProformOnlyApplied(doc, proformId);
        if (sumProform < 0.01) {
          await Swal.fire({
            icon: "warning",
            title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
            text: this.$tn("financialSummary.fobNotaComercial.dragApplyNoLines"),
          });
          return;
        }
        const dragMaxFromUi = Number(parsed && parsed.maxAmount);
        const dragMax = Math.min(
          Number.isFinite(dragMaxFromUi) && dragMaxFromUi > 0 ? dragMaxFromUi : sumProform,
          sumProform
        );

        const ships = (this.collectionStagesVisual && this.collectionStagesVisual.stage2Shippings) || [];
        const csv = this.collectionStagesVisual;
        const shipLabel =
          (ships.find((s) => String(s.shippingId) === String(targetShipping)) || {}).name || targetShipping;
        const shipRow = ships.find((s) => String(s.shippingId) === String(targetShipping));
        let effectiveMax = dragMax;
        let htmlContent = "";
        const pendingInvoice =
          shipRow != null && shipRow.pendingToCollect != null
            ? Math.max(0, Number(shipRow.pendingToCollect) || 0)
            : null;

        if (zone === "invoice") {
          if (pendingInvoice != null && Number.isFinite(pendingInvoice)) {
            effectiveMax = Math.min(dragMax, pendingInvoice);
          } else {
            effectiveMax = dragMax;
          }
        } else if (shipRow && shipRow.expectedAdvanceOnShipping != null) {
          const expected = Number(shipRow.expectedAdvanceOnShipping);
          const assigned = Number(shipRow.advanceAssignedAmount) || 0;
          if (Number.isFinite(expected) && expected > 0) {
            const remainingToExpected = Math.max(0, expected - assigned);
            effectiveMax = Math.min(dragMax, remainingToExpected);
          }
        }

        if (effectiveMax < 0.01) {
          await Swal.fire({
            icon: "warning",
            title: this.$tn("financialSummary.stages.redistributeTitle"),
            text:
              zone === "invoice"
                ? this.$tn("financialSummary.stages.redistributeNoRoomOnShipInvoice")
                : this.$tn("financialSummary.stages.redistributeNoRoomOnShip"),
          });
          return;
        }

        if (zone === "invoice") {
          const pendingLine =
            shipRow && shipRow.pendingToCollect != null
              ? "$" + this.formatNumber(Math.max(0, Number(shipRow.pendingToCollect) || 0))
              : "—";
          htmlContent = `<div class="text-start small text-muted">${this.$t(
            "financialSummary.stages.redistributeToShipInvoiceOnlyHtml",
            {
              ship: String(shipLabel),
              max: (this.moneyPrefix || "$") + this.formatNumber(effectiveMax),
              pending: pendingLine,
            }
          )}</div>`;
        } else {
          const pct = csv && csv.stage1 && csv.stage1.advancePct != null ? csv.stage1.advancePct : "—";
          const expectedNum =
            shipRow && shipRow.expectedAdvanceOnShipping != null
              ? Number(shipRow.expectedAdvanceOnShipping)
              : NaN;
          const assignedNum = shipRow ? Number(shipRow.advanceAssignedAmount) || 0 : 0;
          const expectedLine =
            Number.isFinite(expectedNum) && expectedNum > 0
              ? `<span style="color:#0d6efd;font-weight:800;white-space:nowrap">${pct}%</span>${this.$t(
                  "financialSummary.stages.shipExpectedAdvanceAfterPct",
                  {
                    amount: "$" + this.formatNumber(expectedNum),
                  }
                )}`
              : "—";
          htmlContent = `
<div class="text-start small mb-3 border rounded p-2 bg-light">
  <div class="fw-bold text-uppercase" style="font-size:0.65rem;letter-spacing:0.04em;color:#6c757d">${this.$t(
            "financialSummary.stages.shipStage2AdvanceBlockTitle"
          )}</div>
  <div class="text-muted text-uppercase mt-2" style="font-size:0.58rem;letter-spacing:0.03em">${this.$t(
            "financialSummary.stages.shipAdvanceMetaCaption"
          )}</div>
  <div class="mb-2">${expectedLine}</div>
  <div class="d-flex justify-content-between align-items-baseline gap-2 flex-wrap">
    <span class="text-body small">${this.$t("financialSummary.stages.shipAdvanceAssignedLabel")}</span>
    <span class="fw-semibold">${"$" + this.formatNumber(assignedNum)}</span>
  </div>
</div>
<div class="text-start small text-muted">${this.$t("financialSummary.stages.redistributeToShipHtml", {
            ship: String(shipLabel),
            max: (this.moneyPrefix || "$") + this.formatNumber(effectiveMax),
          })}</div>`;
        }

        const amount = await this.fobNcSwalRedistributeAmount(htmlContent, effectiveMax);
        if (amount == null) return;

        const linesRaw = Array.isArray(doc.applied_to) ? doc.applied_to : [];
        const updatedLines = this.fobNcBuildLinesPartialProformToShipping(
          linesRaw,
          proformId,
          targetShipping,
          amount
        );
        if (!updatedLines) {
          await Swal.fire({
            icon: "error",
            title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
            text: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          });
          return;
        }

        this.redistributeInProgress = true;
        try {
          await this.fobNcPutNotaWithAppliedLines(nid, doc, updatedLines);
          this.$emit("refresh");
        } finally {
          this.redistributeInProgress = false;
        }
      } catch (error) {
        await Swal.fire({
          icon: "error",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            this.$tn("financialSummary.fobNotaComercial.errorTitle"),
        });
      }
    },
    /**
     * Igual que cobros al volver a la proforma: cupo bolsillo etapa 1 + Swal con importe.
     * @param {{ kind: string; notaId: string; shippingId: string; maxAmount?: number }} parsed
     */
    async promptNotaShippingLinesToProform(parsed) {
      const nid = parsed && parsed.notaId != null ? String(parsed.notaId).trim() : "";
      const fromShip =
        parsed && parsed.shippingId != null && parsed.shippingId !== ""
          ? String(parsed.shippingId)
          : "";
      if (!nid || !fromShip) return;
      if (!this.fobNcShortcutUiAllowed) return;
      const proformId =
        this.itemData && this.itemData._id != null && this.itemData._id !== ""
          ? String(this.itemData._id)
          : "";
      if (!proformId) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text: this.$tn("financialSummary.fobNotaComercial.needProform"),
        });
        return;
      }
      if (this.stage1HeaderDone) {
        await Swal.fire({
          icon: "info",
          title: this.$tn("financialSummary.stages.redistributeTitle"),
          text: this.$tn("financialSummary.stages.redistributeReturnToProformBlockedAdvanceComplete"),
        });
        return;
      }
      try {
        const res = await wencesApi.get(`/api/gotrading/dynamic/notacomercial/${encodeURIComponent(nid)}`);
        const doc = this.fobNcUnwrapDynamicGet(res);
        if (!doc || !doc._id) {
          await Swal.fire({
            icon: "error",
            title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
            text: this.$tn("financialSummary.fobNotaComercial.editLoadFailed"),
          });
          return;
        }
        const sumShip = this.fobNcSumShippingMatchApplied(doc, fromShip, proformId);
        if (sumShip < 0.01) {
          await Swal.fire({
            icon: "warning",
            title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
            text: this.$tn("financialSummary.fobNotaComercial.dragReturnNoLines"),
          });
          return;
        }
        const dragMaxFromUi = Number(parsed && parsed.maxAmount);
        const dragMax = Math.min(
          Number.isFinite(dragMaxFromUi) && dragMaxFromUi > 0 ? dragMaxFromUi : sumShip,
          sumShip
        );

        const csv = this.collectionStagesVisual;
        const s1 = csv && csv.stage1;
        const expected = s1 ? Number(s1.expectedAmount) || 0 : 0;
        let pocketUsed = 0;
        if (s1 && s1.proformaOnlyCollectedAmount != null && s1.proformaOnlyCollectedAmount !== "") {
          pocketUsed = Number(s1.proformaOnlyCollectedAmount) || 0;
        } else {
          pocketUsed = (csv.stage1CollectLines || []).reduce(
            (sum, x) => sum + Number(x.totalAmount || 0),
            0
          );
        }
        const roomProformaPocket = Math.max(0, expected - pocketUsed);
        const rem = s1 ? Math.max(0, Number(s1.remainingAmount) || 0) : 0;
        const effectiveMax = Math.min(dragMax, roomProformaPocket, rem);

        if (effectiveMax < 0.01) {
          await Swal.fire({
            icon: "info",
            title: this.$tn("financialSummary.stages.redistributeTitle"),
            text: this.$tn("financialSummary.stages.redistributeNoRoomNoChange"),
          });
          return;
        }

        const remainderStays = effectiveMax < dragMax - 0.005;
        const extraNote =
          remainderStays && effectiveMax > 0.01
            ? `<div class="text-start small text-muted mt-2">${this.$t(
                "financialSummary.stages.redistributeToProformRemainderNote"
              )}</div>`
            : "";
        const htmlContent = `<div class="text-start small">${this.$t(
          "financialSummary.stages.redistributeToProformHtml",
          { max: "$" + this.formatNumber(effectiveMax) }
        )}</div>${extraNote}`;

        const amount = await this.fobNcSwalRedistributeAmount(htmlContent, effectiveMax);
        if (amount == null) return;

        const linesRaw = Array.isArray(doc.applied_to) ? doc.applied_to : [];
        const updatedLines = this.fobNcBuildLinesPartialShippingToProform(
          linesRaw,
          fromShip,
          proformId,
          amount
        );
        if (!updatedLines) {
          await Swal.fire({
            icon: "error",
            title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
            text: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          });
          return;
        }

        this.redistributeInProgress = true;
        try {
          await this.fobNcPutNotaWithAppliedLines(nid, doc, updatedLines);
          this.$emit("refresh");
        } finally {
          this.redistributeInProgress = false;
        }
      } catch (error) {
        await Swal.fire({
          icon: "error",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            this.$tn("financialSummary.fobNotaComercial.errorTitle"),
        });
      }
    },
    closeFobNcEditModal() {
      this.showFobNcEditModal = false;
      this.fobNcEditDoc = null;
      this.fobNcEditContext = null;
      this.fobNcEditAmount = "";
      this.fobNcEditDate = "";
      this.fobNcEditCurrencyId = "";
      this.fobNcEditComment = "";
      this.fobNcEditSaving = false;
      this.fobNcEditDeleting = false;
      this.fobNcEditFetching = false;
    },
    /**
     * @param {object} noteSummary — fila de `linkedNotasComerciales` (incluye _id, appliedTotal).
     * @param {{ mode: 'proform' | 'shipping'; shippingId?: string }} ctx
     */
    async openFobNcEditModal(noteSummary, ctx) {
      if (!this.fobNcShortcutUiAllowed || !noteSummary || !noteSummary._id) return;
      if (!ctx || (ctx.mode !== "proform" && ctx.mode !== "shipping")) return;
      if (!this.fobNcLinkedNoteIsCreditNote(noteSummary)) return;
      const nid = String(noteSummary._id);
      const proformId =
        this.itemData && this.itemData._id != null && this.itemData._id !== ""
          ? String(this.itemData._id)
          : "";
      if (ctx.mode === "proform" && !proformId) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text: this.$tn("financialSummary.fobNotaComercial.needProform"),
        });
        return;
      }
      if (ctx.mode === "shipping" && (!ctx.shippingId || String(ctx.shippingId).trim() === "")) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text: this.$tn("financialSummary.fobNotaComercial.needShipping"),
        });
        return;
      }
      await this.fobNcEnsureLists();
      if (!this.fobNcCurrencyRows().length) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text: this.$tn("financialSummary.fobNotaComercial.needCurrencyLists"),
        });
        return;
      }
      this.fobNcEditFetching = true;
      try {
        const res = await wencesApi.get(`/api/gotrading/dynamic/notacomercial/${encodeURIComponent(nid)}`);
        const doc = this.fobNcUnwrapDynamicGet(res);
        if (!doc || !doc._id) {
          await Swal.fire({
            icon: "error",
            title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
            text: this.$tn("financialSummary.fobNotaComercial.editLoadFailed"),
          });
          return;
        }
        const editCtx =
          ctx.mode === "proform"
            ? { mode: "proform", proformId }
            : { mode: "shipping", shippingId: String(ctx.shippingId) };
        const lines = Array.isArray(doc.applied_to) ? doc.applied_to : [];
        const hasMatch = lines.some((ln) => this.fobNcAppliedLineMatchesEditContext(ln, editCtx));
        if (!hasMatch) {
          await Swal.fire({
            icon: "error",
            title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
            text: this.$tn("financialSummary.fobNotaComercial.editLoadFailed"),
          });
          return;
        }
        this.fobNcEditDoc = doc;
        this.fobNcEditContext = editCtx;
        const at = Number(noteSummary.appliedTotal);
        this.fobNcEditAmount = Number.isFinite(at) && at > 0 ? String(at) : "";
        this.fobNcEditDate = this.fobNcDateToInput(doc.date) || localCalendarIsoDate();
        this.fobNcEditCurrencyId = this.fobNcRefId(doc.currency) || this.fobNcDefaultCurrencyId();
        this.fobNcEditComment = doc.comment != null ? String(doc.comment) : "";
        this.showFobNcEditModal = true;
      } catch (e) {
        await Swal.fire({
          icon: "error",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text: e?.response?.data?.error || e?.message || this.$tn("financialSummary.fobNotaComercial.editLoadFailed"),
        });
      } finally {
        this.fobNcEditFetching = false;
      }
    },
    async submitFobNcEdit() {
      if (!this.fobNcEditDoc || !this.fobNcEditContext) return;
      const nid = String(this.fobNcEditDoc._id);
      const amt = Number(this.fobNcEditAmount);
      if (!Number.isFinite(amt) || amt <= 0) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text: this.$tn("financialSummary.fobNotaComercial.needAmount"),
        });
        return;
      }
      const doc = this.fobNcEditDoc;
      const linesRaw = Array.isArray(doc.applied_to) ? doc.applied_to : [];
      const updatedLines = linesRaw.map((line) => {
        if (!this.fobNcAppliedLineMatchesEditContext(line, this.fobNcEditContext)) return line;
        return { ...line, amount: amt };
      });
      const sumLines = updatedLines.reduce((s, l) => {
        const m = Number(l.amount);
        return s + (Number.isFinite(m) && !Number.isNaN(m) ? m : 0);
      }, 0);
      const applied_to = updatedLines.map((l) => ({
        ...(l._id ? { _id: l._id } : {}),
        shipping: this.fobNcRefId(l.shipping) || null,
        proform: this.fobNcRefId(l.proform) || null,
        amount: Number(l.amount) || 0,
      }));
      const payload = {
        name: doc.name != null ? String(doc.name) : "",
        date: this.fobNcEditDate || localCalendarIsoDate(),
        contact: this.fobNcRefId(doc.contact),
        currency: this.fobNcEditCurrencyId || null,
        credit_debit: doc.credit_debit,
        flow_direction: doc.flow_direction,
        contact_role: doc.contact_role,
        amount: sumLines > 0.005 ? sumLines : amt,
        comment: this.fobNcEditComment != null ? String(this.fobNcEditComment) : "",
        applied_to,
      };
      this.fobNcEditSaving = true;
      try {
        await wencesApi.put(`/api/gotrading/dynamic/notacomercial/${encodeURIComponent(nid)}`, payload);
        this.closeFobNcEditModal();
        await Swal.fire({
          icon: "success",
          title: this.$tn("financialSummary.fobNotaComercial.editSuccessTitle"),
          text: this.$tn("financialSummary.fobNotaComercial.editSuccessText"),
        });
        this.$emit("refresh");
      } catch (error) {
        await Swal.fire({
          icon: "error",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text: error?.response?.data?.error || error?.message || this.$tn("financialSummary.fobNotaComercial.errorTitle"),
        });
      } finally {
        this.fobNcEditSaving = false;
      }
    },
    async deleteFobNcEditNote() {
      if (!this.fobNcEditDoc || !this.fobNcEditDoc._id) return;
      const nid = String(this.fobNcEditDoc._id);
      const result = await Swal.fire({
        icon: "warning",
        title: this.$tn("financialSummary.fobNotaComercial.deleteConfirmTitle"),
        text: this.$tn("financialSummary.fobNotaComercial.deleteConfirmText"),
        showCancelButton: true,
        confirmButtonText: this.$tn("YesDeleteIt"),
        cancelButtonText: this.$tn("cancel"),
        confirmButtonColor: "#dc3545",
      });
      if (!result.isConfirmed) return;
      this.fobNcEditDeleting = true;
      try {
        await wencesApi.delete(
          `/api/gotrading/dynamic/notacomercial/${encodeURIComponent(nid)}/permanent`
        );
        this.closeFobNcEditModal();
        await Swal.fire({
          icon: "success",
          title: this.$tn("financialSummary.fobNotaComercial.deleteSuccessTitle"),
          text: this.$tn("financialSummary.fobNotaComercial.deleteSuccessText"),
        });
        this.$emit("refresh");
      } catch (error) {
        await Swal.fire({
          icon: "error",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text: error?.response?.data?.message || error?.response?.data?.error || error?.message || this.$tn("financialSummary.fobNotaComercial.errorTitle"),
        });
      } finally {
        this.fobNcEditDeleting = false;
      }
    },
    fobNcLinkedNotesForShipping(ship) {
      if (!ship || ship.shippingId == null) return [];
      const fd = this.financialData;
      const bag = fd && fd.linkedNotasComerciales && fd.linkedNotasComerciales.supplierByShipping;
      if (!bag || typeof bag !== "object") return [];
      const sid = String(ship.shippingId);
      const arr = bag[sid];
      return Array.isArray(arr) ? arr : [];
    },
    fobNcLinkedClientNotesOnShipping(ship) {
      if (!ship || ship.shippingId == null) return [];
      const fd = this.financialData;
      const bag = fd && fd.linkedNotasComerciales && fd.linkedNotasComerciales.clientByShipping;
      if (!bag || typeof bag !== "object") return [];
      const sid = String(ship.shippingId);
      const arr = bag[sid];
      return Array.isArray(arr) ? arr : [];
    },
    /** Notas de cliente que reducen lo pendiente de este embarque. */
    fobNcLinkedClientNotesOnShippingCreditOnly(ship) {
      return this.fobNcLinkedClientNotesOnShipping(ship).filter(
        (n) => this.fobNcNoteAppliedKind(n) === "credit"
      );
    },
    /** Notas de cliente que aumentan lo pendiente de este embarque. */
    fobNcLinkedClientNotesOnShippingDebitOnly(ship) {
      return this.fobNcLinkedClientNotesOnShipping(ship).filter(
        (n) => this.fobNcNoteAppliedKind(n) === "debit"
      );
    },
    /** Texto comparable del rol: lo escriben el alta de la nota y la lista de tipos de gasto. */
    fobNcRoleKey(value) {
      return String(value == null ? "" : value)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
    },
    /** NC de ese gasto (las que le bajan lo que hay que pagar). */
    fobNcLinePaymentNotesCreditOnly(ship, line) {
      const label = FOB_NC_RULES.supplier_credit_to_us.credit_debit;
      return this.fobNcLinkedSupplierNotesForPaymentLine(ship, line).filter(
        (n) => String(n && n.credit_debit) === label
      );
    },
    /** ND de ese gasto (las que se lo suben). */
    fobNcLinePaymentNotesDebitOnly(ship, line) {
      const label = FOB_NC_RULES.supplier_debit_to_us.credit_debit;
      return this.fobNcLinkedSupplierNotesForPaymentLine(ship, line).filter(
        (n) => String(n && n.credit_debit) === label
      );
    },
    /** Suma aplicada a ese gasto por las notas de una lista. */
    fobNcSumAppliedTotal(notes) {
      return (Array.isArray(notes) ? notes : []).reduce(
        (sum, n) => sum + (Number(n && n.appliedTotal) || 0),
        0
      );
    },
    fobNcLineNotaCreditApplied(ship, line) {
      return this.fobNcSumAppliedTotal(this.fobNcLinePaymentNotesCreditOnly(ship, line));
    },
    fobNcLineNotaDebitApplied(ship, line) {
      return this.fobNcSumAppliedTotal(this.fobNcLinePaymentNotesDebitOnly(ship, line));
    },
    /**
     * Efecto neto de las notas sobre lo que queda por pagar de este gasto.
     * Negativo si lo bajan (NC), positivo si lo suben (ND).
     */
    fobNcLineNotaNetEffect(ship, line) {
      return (
        this.fobNcLineNotaDebitApplied(ship, line) -
        this.fobNcLineNotaCreditApplied(ship, line)
      );
    },
    /** Texto del efecto, con el signo delante: «−$200.0000». */
    fobNcNotaEffectLabel(effect) {
      const value = Number(effect) || 0;
      const sign = value < 0 ? "−" : "+";
      return `${sign}${this.moneyPrefix || "$"}${this.formatNumber(Math.abs(value))}`;
    },
    /** ¿Hay algo que mostrar del efecto de las notas en esta línea? */
    fobNcLineNotaImpactShow(ship, line) {
      return (
        this.fobNcLineNotaCreditApplied(ship, line) > 0.01 ||
        this.fobNcLineNotaDebitApplied(ship, line) > 0.01
      );
    },

    /**
     * Notas del proveedor que le corresponden a una línea de pago del embarque.
     *
     * Cada línea es un gasto y cada nota trae el rol del contacto: sin separarlas, el
     * bloque de pagos solo mostraba las de la factura de la mercadería y las del flete o
     * el courier no aparecían en ningún lado.
     *
     * @param {object} ship
     * @param {object} line línea de shippingPaymentExpenseLines
     * @returns {object[]}
     */
    fobNcLinkedSupplierNotesForPaymentLine(ship, line) {
      const roleKey = this.fobNcRoleKey(line && line.notaRoleKey);
      if (!roleKey) return [];
      return this.fobNcLinkedNotesForShipping(ship).filter(
        (n) => this.fobNcRoleKey(n && n.contact_role) === roleKey
      );
    },
    fobNcNotaComercialViewHref(id) {
      return `/dynamic-model/notacomercial/view/${encodeURIComponent(id)}`;
    },
    fobNcLinkedNotePillLabel(n) {
      const num = n && n.number != null && String(n.number).trim() !== "" ? String(n.number).trim() : "";
      if (num) return `#${num}`;
      const nm = n && n.name && String(n.name).trim() ? String(n.name).trim() : "";
      if (nm) return nm.length > 20 ? `${nm.slice(0, 17)}…` : nm;
      const idStr = n && n._id ? String(n._id) : "";
      return idStr ? `…${idStr.slice(-6)}` : "—";
    },
    /** Pastillas / título visible: prioriza `name`; si no hay, mismo criterio que `fobNcLinkedNotePillLabel` (#número, id). */
    fobNcLinkedNoteDisplayName(n, maxLen = 28) {
      if (!n) return "—";
      const nm = n.name != null && String(n.name).trim() !== "" ? String(n.name).trim() : "";
      if (nm) {
        const m = Number(maxLen) > 8 ? Math.floor(Number(maxLen)) : 28;
        return nm.length > m ? `${nm.slice(0, Math.max(3, m - 3))}…` : nm;
      }
      return this.fobNcLinkedNotePillLabel(n);
    },
    fobNcLinkedNoteTitleAttr(n) {
      if (!n) return "";
      const parts = [];
      if (n.name) parts.push(String(n.name));
      if (n.credit_debit) parts.push(String(n.credit_debit));
      const amt = Number(n.appliedTotal);
      if (Number.isFinite(amt)) parts.push(`$${this.formatNumber(amt)}`);
      return parts.join(" · ");
    },
    /**
     * Abre el popup único de nota comercial (el mismo del bloque Finance).
     * Antes cada atajo abría un popup chico propio, con solo importe / fecha / moneda.
     * @param {object} context ver NotaComercialNoteModal.open
     * @returns {boolean} false si el popup no está montado en esta pantalla
     */
    fobNcOpenSharedModal(context) {
      const modal = this.$refs.notaComercialNoteModal;
      if (!modal || typeof modal.open !== "function") return false;
      modal.open(context);
      return true;
    },
    async openFobNotaComercialClientModal() {
      if (!this.fobNcShortcutUiAllowed) return;
      const pid =
        this.itemData && this.itemData._id != null && this.itemData._id !== ""
          ? String(this.itemData._id)
          : "";
      if (!pid) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text: this.$tn("financialSummary.fobNotaComercial.needProform"),
        });
        return;
      }
      this.fobNcOpenSharedModal({
        sourceModel: "proform",
        modelName: "proform",
        itemData: this.itemData,
        clientId: this.collectionStagesResolvedClientId || "",
        noteKind: "client_credit_ours",
        currencyId: this.fobNcDefaultCurrencyId(),
        namePrefix: "NC-FOB",
      });
    },
    async openFobNotaComercialClientModalForShipping(ship) {
      if (!this.fobNcShortcutUiAllowed || !ship) return;
      const sid = ship.shippingId != null ? String(ship.shippingId) : "";
      if (!sid) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text: this.$tn("financialSummary.fobNotaComercial.needShipping"),
        });
        return;
      }
      this.fobNcOpenSharedModal({
        sourceModel: "proform",
        modelName: "proform",
        itemData: this.itemData,
        clientId: this.collectionStagesResolvedClientId || "",
        supplierId: this.shippingStage2SupplierContactId(ship) || "",
        noteKind: "client_credit_ours",
        appliedTo: {
          type: "shipping",
          id: sid,
          name: (ship.name && String(ship.name).trim()) || sid,
        },
        currencyId: this.fobNcDefaultCurrencyId(),
        namePrefix: "NC-FOB",
      });
    },
    async openFobNotaComercialSupplierModal(ship) {
      if (!this.fobNcShortcutUiAllowed || !ship) return;
      const sid = ship.shippingId != null ? String(ship.shippingId) : "";
      if (!sid) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text: this.$tn("financialSummary.fobNotaComercial.needShipping"),
        });
        return;
      }
      this.fobNcOpenSharedModal({
        sourceModel: "proform",
        modelName: "proform",
        itemData: this.itemData,
        clientId: this.collectionStagesResolvedClientId || "",
        supplierId: this.shippingStage2SupplierContactId(ship) || "",
        noteKind: "supplier_credit_to_us",
        appliedTo: {
          type: "shipping",
          id: sid,
          name: (ship.name && String(ship.name).trim()) || sid,
        },
        currencyId: this.fobNcDefaultCurrencyId(),
        namePrefix: "NC-FOB",
      });
    },

    /**
     * Nota nueva desde una línea del bloque de pagos.
     *
     * Va por la propia línea de gasto y no por el embarque: así la nota nace con el
     * contacto, el rol y la moneda de ese gasto, igual que desde el botón de la línea en
     * la pantalla del embarque. Antes, desde acá solo se podía cargar contra la factura
     * del proveedor.
     *
     * @param {object} line línea de shippingPaymentExpenseLines
     */
    async openSupplierNoteForPaymentLine(line) {
      const expenseId = line && line.expenseId != null ? String(line.expenseId).trim() : "";
      if (!this.fobNcShortcutUiAllowed || !expenseId) return;
      try {
        const { data: row } = await wencesApi.get(
          `/api/gotrading/dynamic/shipping_expenses/${encodeURIComponent(expenseId)}`
        );
        if (!row || !row._id) return;
        this.fobNcOpenSharedModal({
          ...notaComercialModalContextFromExpenseLine(row, this.lists || []),
          namePrefix: "NC-FOB",
        });
      } catch (error) {
        console.error("CollectionSummary: nota comercial desde la línea de pago", error);
        await Swal.fire({
          icon: "error",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text: this.$tn("financialSummary.fobNotaComercial.addSupplierNoteFailed"),
        });
      }
    },

    /**
     * Saca una nota de una operación a la que se aplicó por error.
     * @param {{_id: string}} note
     * @param {{targetType: 'proform'|'shipping', targetId: string|number, operationName?: string}} target
     */
    async fobNcUnapplyNote(note, target) {
      if (!note || !note._id || !target || target.targetId == null) return;
      const ok = await this.unapplyNotaComercialFromOperation({
        noteId: note._id,
        noteName: this.fobNcLinkedNoteDisplayName(note, 60),
        targetType: target.targetType,
        targetId: target.targetId,
        operationName: target.operationName,
      });
      if (ok) this.$emit("refresh");
    },
  },
};
